import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Grid,
  Modal,
  ModalClose,
  ModalDialog,
  DialogTitle,
  DialogContent,
  Snackbar,
  Textarea,
  Typography,
  ThemeProvider,
  CssBaseline,
  Chip,
} from '@mui/joy';
import Breadcrumbs from '@mui/joy/Breadcrumbs';
import Link from '@mui/joy/Link';
import { KeyboardArrowRight } from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import {
  fetchPendingCustomerRegistrations,
  approveCustomerRegistration,
  rejectCustomerRegistration,
} from '../../main/API/customer';
import { theme } from '../../main/theme/constants';
import CustomTextField from '../../main/widgets/CustomTextField';
import { MuiPhone } from '../../main/widgets/MuiPhone';
import PermissionGate from '../../main/components/PermissionGate';
import { usePermissions } from '../../main/hooks/usePermissions';
import { PERMISSIONS } from '../../main/context/AuthContext';
import { translateApiErrorData, translateAggregateBackendMessage } from '../../main/utils/masterApiError';
import { getPaginatedMeta } from '../../main/utils/pagination';

const STATUS_TABS = ['pending', 'approved', 'rejected', 'all'];

const APPROVE_USER_KEYS = [
  'username',
  'first_name',
  'last_name',
  'first_name_ar',
  'last_name_ar',
  'email',
  'phone_number',
  'id_number',
];

function buildUserPatch(form, initial) {
  const out = {};
  APPROVE_USER_KEYS.forEach((k) => {
    let cur = form[k];
    let ini = initial[k];
    if (typeof cur === 'string') cur = cur.trim();
    if (typeof ini === 'string') ini = ini.trim();
    if (cur === ini) return;
    if (k === 'username' && (cur === '' || cur == null)) {
      out[k] = null;
      return;
    }
    out[k] = cur;
  });
  return out;
}

/** v6: `valueGetter(params)` only. v7: `valueGetter(value, row, column, apiRef)`. */
function valueGetterRow(arg1, arg2) {
  if (arg2 !== undefined) return arg2;
  return arg1?.row;
}

function formatGroups(groups) {
  if (!groups || !Array.isArray(groups)) return '';
  if (groups.length === 0) return '—';
  return groups
    .map((x) => {
      if (x == null) return '';
      if (typeof x === 'string' || typeof x === 'number') return String(x);
      if (typeof x === 'object' && x.name != null) return String(x.name);
      try {
        return JSON.stringify(x);
      } catch {
        return '';
      }
    })
    .filter(Boolean)
    .join(', ');
}

/** Matches the DataGrid styles in CustomerManagerScreen. */
const dataGridSx = {
  border: '1px solid #e0e0e0',
  borderRadius: '8px',
  overflow: 'hidden',
  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  '& .MuiDataGrid-root': {
    border: 'none',
  },
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: '#f5f5f5',
    borderBottom: '2px solid #e0e0e0',
    fontSize: '14px',
    fontWeight: 600,
    color: '#333',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    '& .MuiDataGrid-columnHeaderTitle': {
      fontWeight: 600,
      fontSize: '14px',
      color: '#333',
    },
  },
  '& .MuiDataGrid-cell': {
    whiteSpace: 'normal',
    wordWrap: 'break-word',
    lineHeight: '1.5',
    padding: '12px 16px',
    fontSize: '14px',
    color: '#555',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    borderBottom: '1px solid #f0f0f0',
  },
  '& .MuiDataGrid-row': {
    '&:hover': {
      backgroundColor: '#f8f9fa',
      cursor: 'pointer',
    },
    '&.Mui-selected': {
      backgroundColor: '#e3f2fd',
      '&:hover': {
        backgroundColor: '#bbdefb',
      },
    },
  },
  '& .MuiDataGrid-footerContainer': {
    borderTop: '2px solid #e0e0e0',
    backgroundColor: '#fafafa',
    fontSize: '14px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  },
  '& .MuiDataGrid-toolbarContainer': {
    padding: '12px',
    backgroundColor: '#fafafa',
    borderBottom: '1px solid #e0e0e0',
  },
  '@media (max-width: 600px)': {
    '& .MuiDataGrid-toolbarContainer': {
      flexDirection: 'column',
      gap: 1,
    },
    '& .MuiDataGrid-footer': {
      flexDirection: 'column',
      gap: 1,
    },
    '& .MuiDataGrid-cell': {
      minHeight: '60px',
      alignItems: 'center',
      padding: '8px',
      fontSize: '13px',
    },
    '& .MuiDataGrid-columnHeaders': {
      fontSize: '13px',
    },
  },
};

export default function CustomerRegistrationApprovalsScreen() {
  const { t } = useTranslation('ui');
  const { hasPermission } = usePermissions();
  const canApprove = hasPermission(PERMISSIONS.CUSTOMERS_APPROVE);

  const [statusFilter, setStatusFilter] = useState('pending');
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 100,
  });

  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectTargetId, setRejectTargetId] = useState(null);
  const [rejectNote, setRejectNote] = useState('');
  const [actionId, setActionId] = useState(null);

  const [snackbar, setSnackbar] = useState({ open: false, color: 'success', message: '' });

  const [approveEditOpen, setApproveEditOpen] = useState(false);
  const [approveEditRequestId, setApproveEditRequestId] = useState(null);
  const [approveForm, setApproveForm] = useState({
    username: '',
    first_name: '',
    last_name: '',
    first_name_ar: '',
    last_name_ar: '',
    email: '',
    phone_number: '+968',
    id_number: '',
  });
  const [approveFormErrors, setApproveFormErrors] = useState({});
  const approveInitialRef = useRef(null);

  const load = useCallback(async () => {
    if (!canApprove) {
      setRows([]);
      setRowCount(0);
      return;
    }
    setLoading(true);
    try {
      const page = paginationModel.page + 1;
      const res = await fetchPendingCustomerRegistrations({
        status: statusFilter,
        page,
        page_size: paginationModel.pageSize,
        pagination: true,
      });
      if (!res || res.status !== 200) {
        const msg = translateApiErrorData(res?.data, t) || t('customerRegistrationApprovals.loadFailed');
        setSnackbar({ open: true, color: 'danger', message: msg });
        setRows([]);
        setRowCount(0);
        return;
      }
      const body = res.data;
      const meta = getPaginatedMeta(body, paginationModel.pageSize);
      setRows(
        meta.results.map((item) => ({
          ...item,
          id: item.id,
        }))
      );
      setRowCount(meta.count);
    } finally {
      setLoading(false);
    }
  }, [canApprove, paginationModel.page, paginationModel.pageSize, statusFilter, t]);

  useEffect(() => {
    load();
  }, [load]);

  const handlePageSizeChange = (pageSize) => {
    setPaginationModel({ page: 0, pageSize });
  };

  const handlePageChange = (page) => {
    setPaginationModel((prev) => ({ ...prev, page }));
  };

  const handleApprove = useCallback(
    async (registrationId) => {
      if (!hasPermission(PERMISSIONS.CUSTOMERS_APPROVE)) return;
      setActionId(registrationId);
      try {
        const res = await approveCustomerRegistration(registrationId);
        if (res && res.status === 200) {
          setSnackbar({
            open: true,
            color: 'success',
            message: t('customerRegistrationApprovals.approvedSuccess'),
          });
          load();
        } else {
          setSnackbar({
            open: true,
            color: 'danger',
            message: translateApiErrorData(res?.data, t) || t('customerRegistrationApprovals.actionFailed'),
          });
        }
      } finally {
        setActionId(null);
      }
    },
    [hasPermission, load, t]
  );

  const openApproveEdit = useCallback((row) => {
    const c = row?.customer;
    if (!c) return;
    const form = {
      username: c.username ?? '',
      first_name: c.first_name ?? '',
      last_name: c.last_name ?? '',
      first_name_ar: c.first_name_ar ?? '',
      last_name_ar: c.last_name_ar ?? '',
      email: c.email ?? '',
      phone_number: c.phone_number || '+968',
      id_number: c.id_number ?? '',
    };
    setApproveEditRequestId(row.id);
    setApproveForm(form);
    approveInitialRef.current = { ...form };
    setApproveFormErrors({});
    setApproveEditOpen(true);
  }, []);

  const submitApproveEdit = useCallback(async () => {
    if (approveEditRequestId == null || !approveInitialRef.current) return;
    if (!hasPermission(PERMISSIONS.CUSTOMERS_APPROVE)) return;
    setApproveFormErrors({});
    const patch = buildUserPatch(approveForm, approveInitialRef.current);
    setActionId(approveEditRequestId);
    try {
      const res = await approveCustomerRegistration(
        approveEditRequestId,
        Object.keys(patch).length > 0 ? patch : undefined
      );
      if (res && res.status === 200) {
        setSnackbar({
          open: true,
          color: 'success',
          message: t('customerRegistrationApprovals.approvedSuccess'),
        });
        setApproveEditOpen(false);
        setApproveEditRequestId(null);
        load();
      } else {
        const data = res?.data;
        if (data && typeof data.user === 'object' && data.user !== null) {
          const fieldErrors = {};
          Object.keys(data.user).forEach((key) => {
            const v = data.user[key];
            const raw = Array.isArray(v) ? v[0] : v;
            fieldErrors[key] = translateAggregateBackendMessage(String(raw), t);
          });
          setApproveFormErrors(fieldErrors);
        }
        setSnackbar({
          open: true,
          color: 'danger',
          message: translateApiErrorData(data, t) || t('customerRegistrationApprovals.actionFailed'),
        });
      }
    } finally {
      setActionId(null);
    }
  }, [approveEditRequestId, approveForm, hasPermission, load, t]);

  const openReject = useCallback((registrationId) => {
    setRejectTargetId(registrationId);
    setRejectNote('');
    setRejectOpen(true);
  }, []);

  const confirmReject = async () => {
    if (rejectTargetId == null) return;
    if (!hasPermission(PERMISSIONS.CUSTOMERS_APPROVE)) return;
    setActionId(rejectTargetId);
    try {
      const res = await rejectCustomerRegistration(rejectTargetId, rejectNote);
      if (res && res.status === 200) {
        setSnackbar({
          open: true,
          color: 'success',
          message: t('customerRegistrationApprovals.rejectedSuccess'),
        });
        setRejectOpen(false);
        setRejectTargetId(null);
        load();
      } else {
        setSnackbar({
          open: true,
          color: 'danger',
          message: translateApiErrorData(res?.data, t) || t('customerRegistrationApprovals.actionFailed'),
        });
      }
    } finally {
      setActionId(null);
    }
  };

  const tabLabel = useCallback(
    (s) => t(`customerRegistrationApprovals.status.${s}`),
    [t]
  );

  const yn = useCallback(
    (v) => (v ? t('customerRegistrationApprovals.yes') : t('customerRegistrationApprovals.no')),
    [t]
  );

  const columns = useMemo(
    () => [
      {
        field: 'id',
        headerName: t('customerRegistrationApprovals.colRequestId'),
        flex: 0.8,
        minWidth: 88,
      },
      {
        field: 'status',
        headerName: t('customerRegistrationApprovals.colStatus'),
        flex: 0.9,
        minWidth: 108,
        renderCell: (params) => {
          const st = params.row?.status ?? params.value;
          if (st == null) return null;
          return (
            <Chip size="sm" variant="soft" color={st === 'pending' ? 'warning' : 'neutral'}>
              {tabLabel(st)}
            </Chip>
          );
        },
      },
      {
        field: 'customer_id',
        headerName: t('customerRegistrationApprovals.colCustomerUserId'),
        flex: 0.9,
        minWidth: 100,
        valueGetter: (a, b) => {
          const row = valueGetterRow(a, b);
          return row?.customer?.id ?? '';
        },
      },
      {
        field: 'username',
        headerName: t('customerRegistrationApprovals.colUsername'),
        flex: 1.1,
        minWidth: 130,
        valueGetter: (a, b) => {
          const row = valueGetterRow(a, b);
          return row?.customer?.username ?? '';
        },
      },
      {
        field: 'first_name',
        headerName: t('customerRegistrationApprovals.colFirstName'),
        flex: 1.1,
        minWidth: 120,
        valueGetter: (a, b) => {
          const row = valueGetterRow(a, b);
          return row?.customer?.first_name ?? '';
        },
      },
      {
        field: 'last_name',
        headerName: t('customerRegistrationApprovals.colLastName'),
        flex: 1.1,
        minWidth: 120,
        valueGetter: (a, b) => {
          const row = valueGetterRow(a, b);
          return row?.customer?.last_name ?? '';
        },
      },
      {
        field: 'first_name_ar',
        headerName: t('customerRegistrationApprovals.colFirstNameAr'),
        flex: 1.1,
        minWidth: 130,
        valueGetter: (a, b) => {
          const row = valueGetterRow(a, b);
          return row?.customer?.first_name_ar ?? '';
        },
      },
      {
        field: 'last_name_ar',
        headerName: t('customerRegistrationApprovals.colLastNameAr'),
        flex: 1.1,
        minWidth: 130,
        valueGetter: (a, b) => {
          const row = valueGetterRow(a, b);
          return row?.customer?.last_name_ar ?? '';
        },
      },
      {
        field: 'phone_number',
        headerName: t('customerRegistrationApprovals.colPhone'),
        flex: 1.2,
        minWidth: 140,
        valueGetter: (a, b) => {
          const row = valueGetterRow(a, b);
          return row?.customer?.phone_number ?? '';
        },
      },
      {
        field: 'email',
        headerName: t('customerRegistrationApprovals.colEmail'),
        flex: 1.2,
        minWidth: 160,
        valueGetter: (a, b) => {
          const row = valueGetterRow(a, b);
          return row?.customer?.email ?? '';
        },
      },
      {
        field: 'id_number',
        headerName: t('customerRegistrationApprovals.colIdNumber'),
        flex: 1,
        minWidth: 110,
        valueGetter: (a, b) => {
          const row = valueGetterRow(a, b);
          return row?.customer?.id_number ?? '';
        },
      },
      {
        field: 'groups',
        headerName: t('customerRegistrationApprovals.colGroups'),
        flex: 1.2,
        minWidth: 120,
        sortable: false,
        valueGetter: (a, b) => {
          const row = valueGetterRow(a, b);
          return formatGroups(row?.customer?.groups);
        },
      },
      {
        field: 'is_active',
        headerName: t('customerRegistrationApprovals.colIsActive'),
        flex: 0.75,
        minWidth: 88,
        renderCell: (params) => {
          const active = params.row?.customer?.is_active;
          if (active === undefined || active === null) return '—';
          return (
            <Chip size="sm" variant="soft" color={active ? 'success' : 'neutral'}>
              {yn(active)}
            </Chip>
          );
        },
      },
      {
        field: 'customer_deleted',
        headerName: t('customerRegistrationApprovals.colCustomerDeleted'),
        flex: 0.85,
        minWidth: 100,
        valueGetter: (a, b) => {
          const row = valueGetterRow(a, b);
          if (!row?.customer) return '—';
          return yn(!!row.customer.deleted);
        },
      },
      {
        field: 'created_at',
        headerName: t('customerRegistrationApprovals.colCreated'),
        flex: 1.1,
        minWidth: 170,
        valueGetter: (a, b) => {
          const row = valueGetterRow(a, b);
          return row?.created_at ? new Date(row.created_at).toLocaleString() : '';
        },
      },
      {
        field: 'modified_at',
        headerName: t('customerRegistrationApprovals.colModified'),
        flex: 1.1,
        minWidth: 170,
        valueGetter: (a, b) => {
          const row = valueGetterRow(a, b);
          return row?.modified_at ? new Date(row.modified_at).toLocaleString() : '';
        },
      },
      {
        field: 'resolution_note',
        headerName: t('customerRegistrationApprovals.colResolutionNote'),
        flex: 1.3,
        minWidth: 160,
        sortable: false,
        valueGetter: (a, b) => {
          const row = valueGetterRow(a, b);
          const n = row?.resolution_note;
          if (n == null || String(n).trim() === '') return '—';
          return n;
        },
      },
      {
        field: 'resolved',
        headerName: t('customerRegistrationApprovals.colResolved'),
        flex: 1.2,
        minWidth: 200,
        sortable: false,
        valueGetter: (a, b) => {
          const row = valueGetterRow(a, b);
          if (!row?.resolved_at) return '—';
          const by = row.resolved_by_username ? `${row.resolved_by_username} · ` : '';
          return `${by}${new Date(row.resolved_at).toLocaleString()}`;
        },
      },
      {
        field: 'request_deleted',
        headerName: t('customerRegistrationApprovals.colRequestDeleted'),
        flex: 0.85,
        minWidth: 100,
        valueGetter: (a, b) => {
          const row = valueGetterRow(a, b);
          if (!row) return '';
          return yn(!!row.deleted);
        },
      },
      {
        field: 'actions',
        headerName: t('customerRegistrationApprovals.colActions'),
        flex: 2,
        minWidth: 340,
        sortable: false,
        renderCell: (params) => {
          const r = params.row;
          if (!r) return null;
          const pending = r.status === 'pending';
          if (!pending) return null;
          if (!canApprove) return null;
          const busy = actionId === r.id;
          return (
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 0.5,
                alignItems: 'center',
                py: 0.5,
                maxWidth: 420,
              }}
            >
              <Button
                size="sm"
                color="success"
                variant="soft"
                loading={busy}
                onClick={(e) => {
                  e.stopPropagation();
                  handleApprove(r.id);
                }}
              >
                {t('customerRegistrationApprovals.approve')}
              </Button>
              <Button
                size="sm"
                color="primary"
                variant="outlined"
                loading={busy}
                onClick={(e) => {
                  e.stopPropagation();
                  openApproveEdit(r);
                }}
              >
                {t('customerRegistrationApprovals.approveAndEdit')}
              </Button>
              <Button
                size="sm"
                color="danger"
                variant="soft"
                loading={busy}
                onClick={(e) => {
                  e.stopPropagation();
                  openReject(r.id);
                }}
              >
                {t('customerRegistrationApprovals.reject')}
              </Button>
            </Box>
          );
        },
      },
    ],
    [actionId, canApprove, handleApprove, openApproveEdit, openReject, t, tabLabel, yn]
  );

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ display: 'flex' }} m={3}>
        <Box width="100%">
          <Box>
            <Breadcrumbs separator={<KeyboardArrowRight />} aria-label="breadcrumbs">
              {[t('nav.customers'), t('customerRegistrationApprovals.breadcrumbManager')].map((item) => (
                <Link key={item} color="neutral" href="/customers">
                  {item}
                </Link>
              ))}
            </Breadcrumbs>
            <Typography textAlign="start" level="h1">
              {t('customerRegistrationApprovals.pageTitle')}
            </Typography>
          </Box>

          <PermissionGate permission={PERMISSIONS.CUSTOMERS_APPROVE}>
            <Grid container spacing={1} sx={{ flexGrow: 1, mb: 1 }}>
              <Grid item xs={12}>
                <Typography level="h3" textAlign="start">
                  {t('customerRegistrationApprovals.filterHeading')}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1,
                    alignItems: 'center',
                  }}
                >
                  {STATUS_TABS.map((s) => (
                    <Button
                      key={s}
                      size="sm"
                      variant={statusFilter === s ? 'solid' : 'outlined'}
                      color={statusFilter === s ? 'primary' : 'neutral'}
                      onClick={() => {
                        setStatusFilter(s);
                        setPaginationModel((prev) => ({ ...prev, page: 0 }));
                      }}
                    >
                      {tabLabel(s)}
                    </Button>
                  ))}
                </Box>
              </Grid>
            </Grid>

            <Box>
              <DataGrid
              onPaginationModelChange={(params) => {
                if (params.pageSize !== paginationModel.pageSize) {
                  handlePageSizeChange(params.pageSize);
                } else {
                  handlePageChange(params.page);
                }
              }}
              pagination
              paginationModel={paginationModel}
              paginationMode="server"
              rowCount={rowCount}
              rows={rows || []}
              columns={columns}
              loading={loading}
              initialState={{ pagination: { paginationModel } }}
              pageSizeOptions={[25, 50, 100]}
              hideFooterSelectedRowCount
              getRowHeight={() => 'auto'}
              disableRowSelectionOnClick
              sx={dataGridSx}
            />
            </Box>
          </PermissionGate>

          <Modal
            open={approveEditOpen}
            onClose={() => {
              if (!actionId) setApproveEditOpen(false);
            }}
          >
            <ModalDialog
              sx={{
                width: { xs: '100%', sm: '90%', md: '720px' },
                maxWidth: '720px',
                maxHeight: { xs: '100vh', sm: '90vh' },
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }}
            >
              <ModalClose />
              <DialogTitle sx={{ flexShrink: 0 }}>
                {t('customerRegistrationApprovals.approveEditTitle')}
              </DialogTitle>
              <Typography level="body-sm" sx={{ color: 'text.secondary', mb: 1, px: 2, flexShrink: 0 }}>
                {t('customerRegistrationApprovals.approveEditSubtitle')}
              </Typography>
              <DialogContent sx={{ overflowY: 'auto', flex: 1, minHeight: 0, pr: 0.5 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <CustomTextField
                      label={t('customerRegistrationApprovals.colUsername')}
                      value={approveForm.username}
                      onChange={(e) => {
                        setApproveForm((p) => ({ ...p, username: e.target.value }));
                        if (approveFormErrors.username) setApproveFormErrors((er) => ({ ...er, username: '' }));
                      }}
                      slotProps={{ input: { maxLength: 150 } }}
                    />
                    {approveFormErrors.username && (
                      <Typography level="body-sm" color="danger" sx={{ mt: 0.5 }}>
                        {approveFormErrors.username}
                      </Typography>
                    )}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography level="body-xs" sx={{ mb: 0.5, color: 'neutral.600' }}>
                      {t('customerManager.colPhone')}
                    </Typography>
                    <MuiPhone
                      value={approveForm.phone_number}
                      onChange={(v) => {
                        setApproveForm((p) => ({ ...p, phone_number: v }));
                        if (approveFormErrors.phone_number) setApproveFormErrors((er) => ({ ...er, phone_number: '' }));
                      }}
                    />
                    {approveFormErrors.phone_number && (
                      <Typography level="body-sm" color="danger" sx={{ mt: 0.5 }}>
                        {approveFormErrors.phone_number}
                      </Typography>
                    )}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <CustomTextField
                      label={t('customerManager.labelFirstName')}
                      value={approveForm.first_name}
                      onChange={(e) => {
                        setApproveForm((p) => ({ ...p, first_name: e.target.value }));
                        if (approveFormErrors.first_name) setApproveFormErrors((er) => ({ ...er, first_name: '' }));
                      }}
                      slotProps={{ input: { maxLength: 100 } }}
                    />
                    {approveFormErrors.first_name && (
                      <Typography level="body-sm" color="danger" sx={{ mt: 0.5 }}>
                        {approveFormErrors.first_name}
                      </Typography>
                    )}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <CustomTextField
                      label={t('customerManager.labelLastName')}
                      value={approveForm.last_name}
                      onChange={(e) => {
                        setApproveForm((p) => ({ ...p, last_name: e.target.value }));
                        if (approveFormErrors.last_name) setApproveFormErrors((er) => ({ ...er, last_name: '' }));
                      }}
                      slotProps={{ input: { maxLength: 100 } }}
                    />
                    {approveFormErrors.last_name && (
                      <Typography level="body-sm" color="danger" sx={{ mt: 0.5 }}>
                        {approveFormErrors.last_name}
                      </Typography>
                    )}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <CustomTextField
                      label={t('customerManager.labelFirstNameAr')}
                      value={approveForm.first_name_ar}
                      onChange={(e) => {
                        setApproveForm((p) => ({ ...p, first_name_ar: e.target.value }));
                        if (approveFormErrors.first_name_ar) setApproveFormErrors((er) => ({ ...er, first_name_ar: '' }));
                      }}
                      slotProps={{ input: { maxLength: 100 } }}
                    />
                    {approveFormErrors.first_name_ar && (
                      <Typography level="body-sm" color="danger" sx={{ mt: 0.5 }}>
                        {approveFormErrors.first_name_ar}
                      </Typography>
                    )}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <CustomTextField
                      label={t('customerManager.labelLastNameAr')}
                      value={approveForm.last_name_ar}
                      onChange={(e) => {
                        setApproveForm((p) => ({ ...p, last_name_ar: e.target.value }));
                        if (approveFormErrors.last_name_ar) setApproveFormErrors((er) => ({ ...er, last_name_ar: '' }));
                      }}
                      slotProps={{ input: { maxLength: 100 } }}
                    />
                    {approveFormErrors.last_name_ar && (
                      <Typography level="body-sm" color="danger" sx={{ mt: 0.5 }}>
                        {approveFormErrors.last_name_ar}
                      </Typography>
                    )}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <CustomTextField
                      label={t('customerRegistrationApprovals.colEmail')}
                      type="email"
                      slotProps={{ input: { maxLength: 254 } }}
                      value={approveForm.email}
                      onChange={(e) => {
                        setApproveForm((p) => ({ ...p, email: e.target.value }));
                        if (approveFormErrors.email) setApproveFormErrors((er) => ({ ...er, email: '' }));
                      }}
                    />
                    {approveFormErrors.email && (
                      <Typography level="body-sm" color="danger" sx={{ mt: 0.5 }}>
                        {approveFormErrors.email}
                      </Typography>
                    )}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <CustomTextField
                      label={t('customerManager.labelIdNumber')}
                      value={approveForm.id_number}
                      onChange={(e) => {
                        setApproveForm((p) => ({ ...p, id_number: e.target.value }));
                        if (approveFormErrors.id_number) setApproveFormErrors((er) => ({ ...er, id_number: '' }));
                      }}
                      slotProps={{ input: { maxLength: 20 } }}
                    />
                    {approveFormErrors.id_number && (
                      <Typography level="body-sm" color="danger" sx={{ mt: 0.5 }}>
                        {approveFormErrors.id_number}
                      </Typography>
                    )}
                  </Grid>
                </Grid>
              </DialogContent>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2, pt: 1, flexShrink: 0 }}>
                <Button
                  variant="plain"
                  color="neutral"
                  onClick={() => setApproveEditOpen(false)}
                  disabled={!!actionId}
                >
                  {t('customerRegistrationApprovals.cancel')}
                </Button>
                <PermissionGate permission={PERMISSIONS.CUSTOMERS_APPROVE}>
                  <Button color="success" loading={!!actionId} onClick={submitApproveEdit}>
                    {t('customerRegistrationApprovals.approveEditSubmit')}
                  </Button>
                </PermissionGate>
              </Box>
            </ModalDialog>
          </Modal>

          <Modal
            open={rejectOpen}
            onClose={() => {
              if (!actionId) setRejectOpen(false);
            }}
          >
            <ModalDialog sx={{ maxWidth: 480 }}>
              <ModalClose />
              <Typography level="h4" sx={{ mb: 1 }}>
                {t('customerRegistrationApprovals.rejectTitle')}
              </Typography>
              <Typography level="body-sm" sx={{ mb: 2, color: 'text.secondary' }}>
                {t('customerRegistrationApprovals.rejectHint')}
              </Typography>
              <Textarea
                minRows={3}
                maxRows={8}
                value={rejectNote}
                onChange={(e) => setRejectNote(e.target.value.slice(0, 2000))}
                placeholder={t('customerRegistrationApprovals.rejectNotePlaceholder')}
              />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
                <Button variant="plain" color="neutral" onClick={() => setRejectOpen(false)} disabled={!!actionId}>
                  {t('customerRegistrationApprovals.cancel')}
                </Button>
                <PermissionGate permission={PERMISSIONS.CUSTOMERS_APPROVE}>
                  <Button color="danger" loading={!!actionId} onClick={confirmReject}>
                    {t('customerRegistrationApprovals.confirmReject')}
                  </Button>
                </PermissionGate>
              </Box>
            </ModalDialog>
          </Modal>

          <Snackbar
            autoHideDuration={3000}
            variant="soft"
            open={snackbar.open}
            color={snackbar.color}
            onClose={(event, reason) => {
              if (reason === 'clickaway') return;
              setSnackbar((s) => ({ ...s, open: false }));
            }}
          >
            {snackbar.message}
          </Snackbar>
        </Box>
      </Box>
    </ThemeProvider>
  );
}
